// src/utils/deviceHelpers.ts
export class DeviceHelpers {
  /**
   * Format device status for display
   */
  public static getStatusText(status?: string): string {
    switch (status) {
      case "online":
        return "Online";
      case "offline":
        return "Offline";
      case "unknown":
      default:
        return "Tidak diketahui";
    }
  }

  /**
   * Get CSS class for device status badge
   */
  public static getStatusBadgeClass(status?: string): string {
    switch (status) {
      case "online":
        return "badge badge-light-success";
      case "offline":
        return "badge badge-light-danger";
      case "unknown":
      default:
        return "badge badge-light-warning";
    }
  }

  /**
   * Get icon for device status
   */
  public static getStatusIcon(status?: string): string {
    switch (status) {
      case "online":
        return "bi-wifi";
      case "offline":
        return "bi-wifi-off";
      case "unknown":
      default:
        return "bi-question-circle";
    }
  }

  /**
   * Mask stream key for secure display
   */
  public static maskStreamKey(streamKey: string): string {
    if (!streamKey) return "-";
    if (streamKey.length <= 8) return streamKey;
    return (
      streamKey.substring(0, 4) +
      "..." +
      streamKey.substring(streamKey.length - 4)
    );
  }

  /**
   * Validate device form data
   */
  public static validateDeviceForm(data: any): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!data.password || data.password.trim().length === 0) {
      errors.push("Password wajib diisi");
    } else if (data.password.length < 3) {
      errors.push("Password minimal 3 karakter");
    } else if (data.password.length > 255) {
      errors.push("Password maksimal 255 karakter");
    }

    if (data.name && data.name.length > 255) {
      errors.push("Nama perangkat maksimal 255 karakter");
    }

    if (data.location && data.location.length > 255) {
      errors.push("Lokasi perangkat maksimal 255 karakter");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Generate device display name
   */
  public static getDeviceDisplayName(device: any): string {
    if (device.name && device.name.trim().length > 0) {
      return device.name;
    }
    return `Perangkat #${device.id}`;
  }

  /**
   * Check if device is recently active (within last hour)
   */
  public static isRecentlyActive(lastSeen?: string): boolean {
    if (!lastSeen) return false;

    const lastSeenTime = new Date(lastSeen).getTime();
    const oneHourAgo = Date.now() - 60 * 60 * 1000;

    return lastSeenTime > oneHourAgo;
  }

  /**
   * Get device age in days
   */
  public static getDeviceAge(createdAt: string): number {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Format device uptime
   */
  public static formatUptime(uptimeSeconds: number): string {
    const days = Math.floor(uptimeSeconds / (24 * 60 * 60));
    const hours = Math.floor((uptimeSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((uptimeSeconds % (60 * 60)) / 60);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  /**
   * Get device priority based on status and age
   */
  public static getDevicePriority(device: any): number {
    let priority = 0;

    // Status priority
    if (device.status === "offline") priority += 100;
    else if (device.status === "unknown") priority += 50;

    // Age priority (newer devices get higher priority)
    const age = this.getDeviceAge(device.created_at);
    priority += Math.max(0, 30 - age);

    // Recent activity priority
    if (this.isRecentlyActive(device.lastSeen)) priority += 20;

    return priority;
  }

  /**
   * Sort devices by priority
   */
  public static sortDevicesByPriority(devices: any[]): any[] {
    return [...devices].sort((a, b) => {
      return this.getDevicePriority(b) - this.getDevicePriority(a);
    });
  }

  /**
   * Export devices to CSV format
   */
  public static exportDevicesToCSV(devices: any[]): void {
    const headers = [
      "ID Perangkat",
      "Nama",
      "Lokasi",
      "Stream Key",
      "Status",
      "Terakhir Terlihat",
      "Dibuat",
      "Diperbarui",
    ];

    const csvContent = [
      headers.join(","),
      ...devices.map((device) =>
        [
          device.id,
          `"${device.name || ""}"`,
          `"${device.location || ""}"`,
          `"${device.stream_key}"`,
          this.getStatusText(device.status),
          device.lastSeen
            ? new Date(device.lastSeen).toLocaleString("id-ID")
            : "",
          new Date(device.created_at).toLocaleString("id-ID"),
          new Date(device.updated_at).toLocaleString("id-ID"),
        ].join(",")
      ),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `iot-devices-${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Export devices to JSON format
   */
  public static exportDevicesToJSON(devices: any[]): void {
    const jsonContent = JSON.stringify(devices, null, 2);
    const blob = new Blob([jsonContent], {
      type: "application/json;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `iot-devices-${new Date().toISOString().slice(0, 10)}.json`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Generate device summary statistics
   */
  public static generateDeviceStats(devices: any[]): any {
    const stats = {
      total: devices.length,
      online: 0,
      offline: 0,
      unknown: 0,
      recentlyActive: 0,
      averageAge: 0,
      oldestDevice: null as any,
      newestDevice: null as any,
    };

    if (devices.length === 0) return stats;

    let totalAge = 0;
    let oldestDate = new Date();
    let newestDate = new Date(0);

    devices.forEach((device) => {
      // Count by status
      switch (device.status) {
        case "online":
          stats.online++;
          break;
        case "offline":
          stats.offline++;
          break;
        default:
          stats.unknown++;
      }

      // Count recently active
      if (this.isRecentlyActive(device.lastSeen)) {
        stats.recentlyActive++;
      }

      // Calculate age
      const createdDate = new Date(device.created_at);
      const age = this.getDeviceAge(device.created_at);
      totalAge += age;

      // Find oldest and newest
      if (createdDate < oldestDate) {
        oldestDate = createdDate;
        stats.oldestDevice = device;
      }
      if (createdDate > newestDate) {
        newestDate = createdDate;
        stats.newestDevice = device;
      }
    });

    stats.averageAge = Math.round(totalAge / devices.length);

    return stats;
  }

  /**
   * Filter devices by various criteria
   */
  public static filterDevices(devices: any[], filters: any): any[] {
    return devices.filter((device) => {
      // Status filter
      if (
        filters.status &&
        filters.status !== "all" &&
        device.status !== filters.status
      ) {
        return false;
      }

      // Search query filter
      if (filters.search) {
        const query = filters.search.toLowerCase();
        const searchableText = [
          device.id.toString(),
          device.name || "",
          device.location || "",
        ]
          .join(" ")
          .toLowerCase();

        if (!searchableText.includes(query)) {
          return false;
        }
      }

      // Date range filter
      if (filters.dateFrom || filters.dateTo) {
        const deviceDate = new Date(device.created_at);

        if (filters.dateFrom && deviceDate < new Date(filters.dateFrom)) {
          return false;
        }

        if (filters.dateTo && deviceDate > new Date(filters.dateTo)) {
          return false;
        }
      }

      // Recently active filter
      if (filters.recentlyActive && !this.isRecentlyActive(device.lastSeen)) {
        return false;
      }

      return true;
    });
  }

  /**
   * Validate stream key format
   */
  public static isValidStreamKey(streamKey: string): boolean {
    if (!streamKey || typeof streamKey !== "string") return false;

    // Check length (should be 16 characters based on Laravel implementation)
    if (streamKey.length !== 16) return false;

    // Check if it contains only alphanumeric characters
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    return alphanumericRegex.test(streamKey);
  }

  /**
   * Generate random color for device visualization
   */
  public static getDeviceColor(deviceId: number): string {
    const colors = [
      "#009ef7",
      "#50cd89",
      "#f1416c",
      "#ffc700",
      "#7239ea",
      "#00a3ff",
      "#17c653",
      "#e63946",
      "#f77f00",
      "#6f42c1",
    ];

    return colors[deviceId % colors.length];
  }

  /**
   * Format device data for display in tables
   */
  public static formatDeviceForTable(device: any): any {
    return {
      ...device,
      displayName: this.getDeviceDisplayName(device),
      statusText: this.getStatusText(device.status),
      statusClass: this.getStatusBadgeClass(device.status),
      maskedStreamKey: this.maskStreamKey(device.stream_key),
      formattedCreatedAt: new Date(device.created_at).toLocaleString("id-ID"),
      formattedUpdatedAt: new Date(device.updated_at).toLocaleString("id-ID"),
      ageInDays: this.getDeviceAge(device.created_at),
      isRecentlyActive: this.isRecentlyActive(device.lastSeen),
      priority: this.getDevicePriority(device),
    };
  }

  /**
   * Sanitize device input data
   */
  public static sanitizeDeviceInput(input: any): any {
    return {
      name: input.name ? input.name.trim().substring(0, 255) : null,
      location: input.location ? input.location.trim().substring(0, 255) : null,
      password: input.password ? input.password.trim() : "",
    };
  }
}
